import type { MouseEventHandler } from 'react';
import { cn } from '@/lib/utils';
import {
  PREPAYMENT_REVIEW_CLS,
  PREPAYMENT_REVIEW_GLYPH,
  PREPAYMENT_REVIEW_TITLE,
  settlementStatusLabel,
  settlementStatusStyle,
  type CashFlowEntry,
} from '@/lib/cashFlow';
import type { SettlementStatus } from '@progear/finance-core';

/** The fields the status cell reads off a `LedgerRow` — a structural subset so the cell
 *  never imports the giant Ledger module. `e` stays on the row shape for the caller; the
 *  cell itself only needs the resolved status + direction (+ the paid share). */
export interface LedgerStatusCellRow {
  e: CashFlowEntry;
  status: SettlementStatus;
  suund: 'income' | 'cost';
  /** Whole-percent paid share — shown in the badge ONLY when partly settled. */
  tasutudPct: number;
  /** Overdue age of a carried-forward row (finance-core `overdueDaysOf` /
   *  `overdueSinceOf`), or null — the badge appends "N p" ONLY when overdue. */
  overdueAge: { days: number; since: string } | null;
  /** True on a stale katteta ettemaks (finance-core `prepaymentReviewNeeded`) —
   *  renders the shared amber "kontrolli" marker beside the status badge. */
  reviewNeeded: boolean;
}

/**
 * The register "Staatus" cell: a single COMPACT badge — a status dot + the
 * direction-aware Estonian label (Laekunud / Makstud / Üle tähtaja …) + the paid
 * percent ONLY when partly settled (no redundant "100%" on a cleared row).
 * makstud↔laekunud stay colour-distinct via `settlementStatusStyle`.
 *
 * The cell is ALSO the hover anchor for the register's delayed settlement/document card:
 * the owning row wires `onMouseEnter` / `onMouseLeave` here so the SAME GroupHoverCard /
 * DocHoverCard the calendar opens (L14) appears when the pointer dwells on the STATUS —
 * not anywhere on the row. That keeps "hover = info" cleanly separate from "row click =
 * open the document", and removes the old lens-local card that overlapped the row tooltip.
 */
export function LedgerStatusCell({
  row,
  onMouseEnter,
  onMouseLeave,
}: {
  row: LedgerStatusCellRow;
  onMouseEnter?: MouseEventHandler<HTMLTableCellElement>;
  onMouseLeave?: MouseEventHandler<HTMLTableCellElement>;
}) {
  const st = settlementStatusStyle(row.status, row.suund);
  return (
    <td className="px-2.5 py-1.5" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <span
        className={cn(
          'inline-flex cursor-default items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium',
          st.badge,
        )}
      >
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', st.dot)} aria-hidden />
        {settlementStatusLabel(row.status, row.suund)}
        {row.status === 'partial' && (
          <span className="tabular-nums font-semibold">{row.tasutudPct}%</span>
        )}
        {row.status === 'overdue' && row.overdueAge !== null && row.overdueAge.days > 0 && (
          <span
            className="tabular-nums font-semibold"
            title={`Üle tähtaja ${row.overdueAge.days} päeva`}
          >
            {row.overdueAge.days} p
          </span>
        )}
      </span>
      {row.reviewNeeded && (
        <span
          data-review-needed
          title={PREPAYMENT_REVIEW_TITLE}
          className={cn('ml-1.5 inline-flex cursor-help py-0.5', PREPAYMENT_REVIEW_CLS)}
        >
          {PREPAYMENT_REVIEW_GLYPH}
        </span>
      )}
    </td>
  );
}
